import type { IngotSchema } from '../llm/llm-input-types';

/**
 * What Ingot's `/add` handed back for one stored tool result, kept on the tool
 * part at `callProviderMetadata.cotera.ingot`. The renderer uses it to decide
 * whether the model sees the output verbatim or only this receipt.
 */
export type IngotToolReceipt = {
  table: string;
  /** SELECT returning exactly the rows this tool call stored. */
  query: string;
  /**
   * The `_batch` every row of this call shares. Absent on receipts stored
   * before it was kept, which show `query` instead.
   */
  batch?: string;
  totalResults: number;
  estimatedTokens: number;
  /** True when the output is small enough to stay in the prompt. */
  inline: boolean;
};

/**
 * Columns the server adds to every tool-result table, whatever the tool's own
 * mapping. Listed once in the schema block rather than on every table.
 */
export const INGOT_TOOL_CALL_COLUMNS = [
  'tool_call_id',
  'conversation_id',
  'tool_key',
  'called_at',
] as const;

/** Tables past this are left out of the schema block so it stays bounded. */
export const MAX_INGOT_SCHEMA_TABLES = 40;

export function parseIngotToolReceipt(value: unknown): IngotToolReceipt | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const table: unknown = Reflect.get(value, 'table');
  const query: unknown = Reflect.get(value, 'query');
  const totalResults: unknown = Reflect.get(value, 'totalResults');
  const estimatedTokens: unknown = Reflect.get(value, 'estimatedTokens');
  const inline: unknown = Reflect.get(value, 'inline');
  const batch: unknown = Reflect.get(value, 'batch');
  if (
    typeof table !== 'string' ||
    typeof query !== 'string' ||
    typeof totalResults !== 'number' ||
    typeof estimatedTokens !== 'number' ||
    typeof inline !== 'boolean'
  ) {
    return null;
  }
  return {
    table,
    query,
    ...(typeof batch === 'string' ? { batch } : {}),
    totalResults,
    estimatedTokens,
    inline,
  };
}

/**
 * What the model sees in place of a tool output that has an Ingot receipt: the
 * output beside the receipt when it is inline, otherwise the receipt alone.
 *
 * `collapse` forces the receipt-only view even for an inline result — used once
 * a later turn has completed, so a result the model already read and summarized
 * stops taking up room in every subsequent prompt. It can always fetch it again
 * through `coco/ingot-query`.
 */
export function ingotModelOutput(
  receipt: IngotToolReceipt,
  output: unknown,
  options?: { collapse?: boolean }
): Record<string, unknown> {
  // The batch rather than a ready-made `SELECT *`: handed a full query, the
  // model tends to run it as-is and pull the whole result back into context.
  const ref = {
    table: receipt.table,
    ...(receipt.batch !== undefined
      ? { batch: receipt.batch }
      : { query: receipt.query }),
    rows: receipt.totalResults,
  };
  const collapse = options?.collapse === true;
  if (receipt.inline && !collapse) {
    return { output, ingot: ref };
  }
  return {
    ingot: { ...ref, estimatedTokens: receipt.estimatedTokens },
    note: 'Use coco/ingot-query to narrow results.',
  };
}

const TOOL_CALL_COLUMN_NAMES: ReadonlySet<string> = new Set(
  INGOT_TOOL_CALL_COLUMNS
);

/** The `<ingot_schema>` block injected on each model turn. */
export function renderIngotSchema(schema: IngotSchema): string {
  if (schema.tables.length === 0) {
    return [
      '<ingot_schema>',
      'This chat has no ingot tables yet — no tool result is stored and no ' +
        'document is attached. There is nothing to query: do not call ' +
        'coco/ingot-query until a tool runs or a document is attached. Tool ' +
        'results and attachments land here automatically as the chat goes on.',
      '</ingot_schema>',
    ].join('\n');
  }
  const tables = schema.tables.slice(0, MAX_INGOT_SCHEMA_TABLES);
  const sections = tables.map((t) => {
    const columns = t.columns.filter(
      (c) => !TOOL_CALL_COLUMN_NAMES.has(c.name)
    );
    return [
      `### ${t.name} — ${t.rows} ${t.rows === 1 ? 'row' : 'rows'}`,
      '| column | type |',
      '| --- | --- |',
      ...columns.map(
        (c) => `| ${c.name} | ${c.type}${c.embedded ? ' (embedded)' : ''} |`
      ),
    ].join('\n');
  });
  const omitted = schema.tables.length - tables.length;
  return [
    '<ingot_schema>',
    'Tables in this chat, queryable with coco/ingot-query. These are the only ' +
      'tables and columns there are: use the names exactly as listed, and ' +
      'quote a name that has uppercase letters or punctuation.',
    '',
    ...sections.flatMap((s) => [s, '']),
    ...(omitted > 0 ? [`(${omitted} more tables not listed)`, ''] : []),
    `- Tool-result tables also have ${INGOT_TOOL_CALL_COLUMNS.join(', ')}.`,
    "- Rows from one tool call share a `_batch`; a receipt's `batch` scopes a query to that call: WHERE _batch = '<batch>'.",
    '- An (embedded) column X has a companion vector X_vec: pass the `text` ' +
      'arg and ORDER BY array_cosine_similarity(X_vec, $q) DESC to match by ' +
      'meaning.',
    "- For typos or partial strings use ILIKE '%term%' or " +
      "jaro_winkler_similarity(lower(col), lower('term')). Reserve exact `=` " +
      'on free text for values you know verbatim.',
    '</ingot_schema>',
  ].join('\n');
}
